import { h, defineComponent, ref, watch } from 'vue';
import { emptyList } from '@/services/api';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { box, loader, pagination } from '@/ui/components';
import { SelectOptions, SortDirection } from '@/ui/types';
import { InspectionNegativeAnswerList } from './InspectionNegativeAnswerList';
import * as qs from '@/ui/queryState';
import { filterListToOptions, sequence } from '@/ui/helpers';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { filterFormLoader } from '@/ui/components/Loader';
import {
  autocompleteChecklistFilter,
  conductedByFilter,
  lastUpdatedFilter,
  searchFilter,
  entityItemFilter,
} from '@/ui/components/filters/filters-horizontal';
import { list } from '@/services/api/resources/question';
import { hasPermission, Permissions, user } from '@/ui/auth/auth';
import { QuestionList } from '@/entities/question';

export const InspectionNegativeAnswerView = defineComponent({
  name: 'InspectionNegativeAnswerView',
  setup() {
    const search = qs.string('q', '');
    const start = qs.nullableDate('start', null);
    const end = qs.nullableDate('end', null);
    const inspector = qs.numbers('inspector', []);
    const entityItem = qs.numbers('entityItem', []);
    const checklist = qs.numbers('checklist', []);

    const records = ref<QuestionList>(emptyList());
    const initialLoading = ref(true);

    const {
      page,
      perPage,
      resetPagination,
    } = usePagination();

    const {
      sort,
      dir,
      onSortChange,
    } = useSorting('conductedAt', SortDirection.Descending);

    const getList = useTask(
      () => list({
        timezone: user.value!.timezone,
        start: start.value,
        end: end.value,
        entityItem: entityItem.value,
        group: [],
        category: [],
        checklist: checklist.value,
        question: [],
        questionTitle: [],
        response: [],
        inspection: [],
        section: [],
        hasScore: false,
        negative: true,
        page: page.value,
        perPage: 20,
        sort: sort.value,
        dir: dir.value,
        search: search.value,
        inspector: inspector.value,
      }),
      listResponse => {
        records.value = listResponse;
      },
    );

    const users = ref<SelectOptions>([]);
    const entityItems = ref<SelectOptions>([]);
    const getFilters = useTask(
      () => sequence(getAllUsers(), getAllEntityItems()),
      ([userList, entityItemList]) => {
        users.value = filterListToOptions(userList);
        entityItems.value = filterListToOptions(entityItemList);
      },
    );

    watch([
      page,
      perPage,
      search,
      checklist,
      start,
      end,
      inspector,
      entityItem,
      sort,
      dir,
    ], getList.run);

    watch(() => getList.state.value.pending, newVal => {
      initialLoading.value = newVal;
    });

    return function render() {
      return h('div', [
        filterFormLoader(getFilters, [
          searchFilter(search, resetPagination),
          hasPermission(Permissions.MANAGE_CHECKLISTS)
            ? autocompleteChecklistFilter(checklist, resetPagination)
            : null,
          lastUpdatedFilter(start, end, resetPagination),
          conductedByFilter(inspector, users.value, resetPagination),
          entityItemFilter(entityItem, entityItems.value, resetPagination),
        ]),
        box([
          loader('div', getList, [
            h(InspectionNegativeAnswerList, {
              props: {
                records: records.value.data,
                loading: getList.state.value.pending,
                sort: sort.value,
                dir: dir.value,
              },
              on: {
                'issue:created': getList.run,
                sort: (field: string, direction: SortDirection) => {
                  onSortChange(field, direction);
                  resetPagination();
                },
              },
            }),
            pagination({
              current: page.value,
              total: records.value.meta.total,
              perPage: perPage.value,
            }),
          ], !initialLoading.value),
        ]),
      ]);
    };
  },
});
